import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { CalendarCheck2, Plus, X } from 'lucide-react';
import {
  ILLNESS_PHASE_LABELS,
  VACCINE_ETC_CODE,
  VACCINE_SCHEDULE,
  type IllnessPhaseCode,
  type VaccineDef,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  ReadOnlyBanner,
  Spinner,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { formatFullDate, todayISO } from '@/lib/format';
import { useCanWrite } from '@/store/auth';
import type { ChildSummary } from '@/types';

interface VaccinationRecord {
  id: string;
  vaccineCode: string;
  doseLabel: string;
  label: string | null;
  vaccinatedOn: string;
  institution: string | null;
}

interface IllnessRecord {
  id: string;
  name: string;
  phase: IllnessPhaseCode;
  occurredOn: string | null;
  note: string | null;
}

function invalidate(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['health-records'] });
}

/**
 * 예방접종·병력 — 명세서 §5.11.
 * 표준예방접종 일정(질병관리청 기준)에 접종일·기관을 채우고, 병력을 남긴다.
 * 여기 입력한 기록이 생활기록부 PDF의 ④표준예방접종·⑤병력기록 칸에 그대로 실린다(§5.13).
 */
export function HealthRecordsPage() {
  const canWrite = useCanWrite();

  const { data: children, isPending } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const [childId, setChildId] = useState('');
  const [tab, setTab] = useState<'vaccine' | 'illness'>('vaccine');
  const activeChildId = childId || children?.[0]?.id || '';

  return (
    <Screen withNav={false}>
      <AppBar title="예방접종·병력" back />

      {isPending && <Spinner />}
      {!isPending && (children?.length ?? 0) === 0 && (
        <EmptyState title="연결된 원아가 없어요" body="원아가 연결되면 접종 이력을 관리할 수 있어요." />
      )}

      {activeChildId && (
        <div className="flex flex-col gap-3 p-4">
          {!canWrite && <ReadOnlyBanner message="지금은 열람만 할 수 있어요." />}

          {(children?.length ?? 0) > 1 && (
            <div className="flex flex-wrap gap-2">
              {children?.map((child) => (
                <Chip
                  key={child.id}
                  tone={child.id === activeChildId ? 'primary' : 'neutral'}
                  selected={child.id === activeChildId}
                  onClick={() => setChildId(child.id)}
                >
                  {child.name}
                </Chip>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Chip tone={tab === 'vaccine' ? 'primary' : 'neutral'} selected={tab === 'vaccine'} onClick={() => setTab('vaccine')}>
              예방접종
            </Chip>
            <Chip tone={tab === 'illness' ? 'primary' : 'neutral'} selected={tab === 'illness'} onClick={() => setTab('illness')}>
              병력
            </Chip>
          </div>

          <p className="text-sm text-ink-sub">
            여기 남긴 기록은 생활기록부의 표준예방접종·병력기록 칸에 자동으로 실려요.
          </p>

          {tab === 'vaccine' ? (
            <VaccineSection childId={activeChildId} canWrite={canWrite} />
          ) : (
            <IllnessSection childId={activeChildId} canWrite={canWrite} />
          )}
        </div>
      )}
    </Screen>
  );
}

// ── 예방접종 — 표준 일정 체크리스트 ─────────────────────────

function VaccineSection({ childId, canWrite }: { childId: string; canWrite: boolean }) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['health-records', 'vaccinations', childId],
    queryFn: () => apiData<VaccinationRecord[]>(`/health/vaccinations?childId=${childId}`),
    placeholderData: keepPreviousData, // 아이 전환 시 목록 유지
  });

  if (isPending) return <Spinner />;
  if (isError) return <ErrorNote message="접종 이력을 불러오지 못했어요." onRetry={() => void refetch()} />;

  const records = data ?? [];
  const done = records.length;

  return (
    <>
      <p className="text-sm text-ink-sub tnum">기록된 접종 {done}건</p>
      {VACCINE_SCHEDULE.map((group) => (
        <Card key={group.disease}>
          <p className="mb-2 font-bold">{group.disease}</p>
          <div className="flex flex-col gap-2">
            {group.vaccines.map((vaccine) => (
              <VaccineRows
                key={vaccine.code}
                childId={childId}
                vaccine={vaccine}
                records={records.filter((r) => r.vaccineCode === vaccine.code)}
                canWrite={canWrite}
              />
            ))}
          </div>
        </Card>
      ))}
    </>
  );
}

function VaccineRows({
  childId,
  vaccine,
  records,
  canWrite,
}: {
  childId: string;
  vaccine: VaccineDef;
  records: VaccinationRecord[];
  canWrite: boolean;
}) {
  const isEtc = vaccine.code === VACCINE_ETC_CODE;

  if (isEtc || vaccine.repeatable) {
    // 매년·기타 접종 — 기록 목록 + 추가
    return (
      <div className="flex flex-col gap-2">
        {vaccine.name && <p className="text-sm text-ink-sub">{vaccine.name} · {vaccine.doses[0]}</p>}
        {records.map((record) => (
          <RecordedRow key={record.id} record={record} canWrite={canWrite} showLabel={isEtc} />
        ))}
        {canWrite && (
          <RecordForm
            childId={childId}
            vaccineCode={vaccine.code}
            doseLabel={vaccine.doses[0]}
            askLabel={isEtc}
            buttonText={isEtc ? '기타 접종 추가' : `${vaccine.name} 접종 추가`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {vaccine.doses.map((dose) => {
        const record = records.find((r) => r.doseLabel === dose);
        return (
          <div key={dose} className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {vaccine.name} <span className="text-ink-sub">{dose}</span>
              </p>
              {record && (
                <p className="text-sm text-primary-strong tnum">
                  {formatFullDate(record.vaccinatedOn)}
                  {record.institution ? ` · ${record.institution}` : ''}
                </p>
              )}
            </div>
            {record ? (
              <DeleteButton id={record.id} kind="vaccinations" canWrite={canWrite} />
            ) : (
              canWrite && <InlineRecordButton childId={childId} vaccineCode={vaccine.code} doseLabel={dose} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecordedRow({
  record,
  canWrite,
  showLabel,
}: {
  record: VaccinationRecord;
  canWrite: boolean;
  showLabel: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2">
      <p className="min-w-0 text-sm">
        {showLabel && record.label && <b>{record.label} · </b>}
        <span className="text-primary-strong tnum">{formatFullDate(record.vaccinatedOn)}</span>
        {record.institution ? ` · ${record.institution}` : ''}
      </p>
      <DeleteButton id={record.id} kind="vaccinations" canWrite={canWrite} />
    </div>
  );
}

/** 미접종 행의 "기록" 버튼 — 누르면 날짜·기관 입력이 펼쳐진다 */
function InlineRecordButton({
  childId,
  vaccineCode,
  doseLabel,
}: {
  childId: string;
  vaccineCode: string;
  doseLabel: string;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button size="sm" variant="outline" icon={<CalendarCheck2 size={14} />} onClick={() => setOpen(true)}>
        기록
      </Button>
    );
  }
  return (
    <RecordForm
      childId={childId}
      vaccineCode={vaccineCode}
      doseLabel={doseLabel}
      askLabel={false}
      buttonText="저장"
      onDone={() => setOpen(false)}
      compact
    />
  );
}

function RecordForm({
  childId,
  vaccineCode,
  doseLabel,
  askLabel,
  buttonText,
  onDone,
  compact,
}: {
  childId: string;
  vaccineCode: string;
  doseLabel: string;
  askLabel: boolean;
  buttonText: string;
  onDone?: () => void;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(Boolean(compact));
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(todayISO());
  const [institution, setInstitution] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api('/health/vaccinations', {
        method: 'POST',
        body: {
          childId,
          vaccineCode,
          doseLabel,
          vaccinatedOn: date,
          ...(askLabel && label.trim() ? { label: label.trim() } : {}),
          ...(institution.trim() ? { institution: institution.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setLabel('');
      setInstitution('');
      invalidate(queryClient);
      onDone?.();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
        {buttonText}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-md bg-primary-soft/40 p-3">
      {askLabel && (
        <Input placeholder="백신 이름 (예: 로타바이러스)" value={label} onChange={(e) => setLabel(e.target.value)} />
      )}
      <div className="flex gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="flex-1" />
        <Input
          placeholder="접종기관 (선택)"
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          className="flex-1"
        />
      </div>
      {error && <p className="text-sm text-notice">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setOpen(Boolean(compact) && false);
            onDone?.();
          }}
        >
          취소
        </Button>
        <Button size="sm" loading={save.isPending} disabled={!date} onClick={() => save.mutate()}>
          저장
        </Button>
      </div>
    </div>
  );
}

// ── 병력 ────────────────────────────────────────────────────

function IllnessSection({ childId, canWrite }: { childId: string; canWrite: boolean }) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['health-records', 'illnesses', childId],
    queryFn: () => apiData<IllnessRecord[]>(`/health/illnesses?childId=${childId}`),
    placeholderData: keepPreviousData,
  });

  if (isPending) return <Spinner />;
  if (isError) return <ErrorNote message="병력을 불러오지 못했어요." onRetry={() => void refetch()} />;

  const records = data ?? [];

  return (
    <>
      {records.length === 0 && (
        <EmptyState title="남긴 병력이 없어요" body="앓았던 질병을 남겨 두면 생활기록부와 인계 서류에 함께 실려요." />
      )}

      {records.map((record) => (
        <Card key={record.id}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold">
                {record.name}{' '}
                <Chip tone="neutral">{ILLNESS_PHASE_LABELS[record.phase]}</Chip>
              </p>
              <p className="mt-1 text-sm text-ink-sub">
                {[record.occurredOn ? formatFullDate(record.occurredOn) : null, record.note]
                  .filter(Boolean)
                  .join(' · ') || '자세한 기록 없음'}
              </p>
            </div>
            <DeleteButton id={record.id} kind="illnesses" canWrite={canWrite} />
          </div>
        </Card>
      ))}

      {canWrite && <IllnessForm childId={childId} />}
    </>
  );
}

function IllnessForm({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<IllnessPhaseCode>('AFTER_ADMISSION');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api('/health/illnesses', {
        method: 'POST',
        body: {
          childId,
          name: name.trim(),
          phase,
          ...(date ? { occurredOn: date } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setName('');
      setDate('');
      setNote('');
      invalidate(queryClient);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-3 font-bold">병력 남기기</p>
      <div className="flex flex-col gap-3">
        <Field label="병명" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 수족구병" />
        </Field>

        <Field label="시기" required>
          <div className="flex gap-2">
            {(Object.keys(ILLNESS_PHASE_LABELS) as IllnessPhaseCode[]).map((code) => (
              <Chip key={code} tone={phase === code ? 'primary' : 'neutral'} selected={phase === code} onClick={() => setPhase(code)}>
                {ILLNESS_PHASE_LABELS[code]}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label="발생 연월일" hint="기억나는 만큼만 적어도 괜찮아요.">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>

        <Field label="정도·경과">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 일주일 통원 후 회복" />
        </Field>

        {error && <p className="text-sm text-notice">{error}</p>}
        <Button block loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
          저장
        </Button>
      </div>
    </Card>
  );
}

function DeleteButton({ id, kind, canWrite }: { id: string; kind: 'vaccinations' | 'illnesses'; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/health/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient),
  });
  if (!canWrite) return null;
  return (
    <button
      type="button"
      aria-label="기록 지우기"
      className="tap shrink-0 rounded-sm p-1.5 text-ink-sub hover:bg-primary-soft"
      onClick={() => {
        if (window.confirm('이 기록을 지울까요?')) remove.mutate();
      }}
    >
      <X size={16} />
    </button>
  );
}
